/**
 * Precios de recobro: los honorarios de una empresa por lo cobrado en el mes
 * (docs/facturacion-cliente-plan.md).
 *
 * Recobro cobra un porcentaje de lo que se recupera, y las reglas son del
 * CLIENTE, no del expediente (contexto/NEGOCIO.md, «El fee de cada cliente»): el
 * fee de la empresa manda sobre `porcentaje_presupuesto`. Por eso viven aquí:
 *
 *   - `extrajudicial_pct`: % sobre los pagos extrajudiciales;
 *   - `judicial_pct`: % sobre los pagos judiciales;
 *   - `bill_judicial`: si se le factura lo judicial (a Max Energía, no);
 *   - `monthly_cap`: tope de honorarios al mes (Anticimex, 800 €). null = sin tope.
 *
 * Las tasas judiciales (suplidos) salen del anexo del despacho y no están en la
 * aplicación: se añaden en la factura. Precios sin IVA.
 *
 * Lo usan el backend (que es la puerta) y el editor del perfil. Las reglas viven
 * solo aquí.
 */

const VAT_RATE = 0.21;
const MAX_TIERS = 20;

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const isPct = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100;
const isMoney = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const isDays = (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0;

/** Si la empresa cobra por escalado de días en vez de un porcentaje plano. */
function hasTiers(pricing) {
  return Boolean(pricing && Array.isArray(pricing.extrajudicial_tiers) && pricing.extrajudicial_tiers.length);
}

/**
 * Los errores de un escalado, en castellano. Vacío = válido.
 *
 * Mismas reglas que los tramos de AR (`ar-pricing.js`), pero en DÍAS de impago y
 * con un porcentaje en vez de un precio: empiezan en 0, van seguidos y solo el
 * último puede no tener límite. Así no hay huecos ni solapes y todo cobro cae en
 * un tramo.
 *
 * **Los límites son INCLUSIVOS**, al contrario que en AR, donde el tramo es
 * `desde ≤ euros < hasta`. En días lo natural es como lo dice el acuerdo —«8 %
 * hasta 30 días, 12 % de 31 a 60, 15 % a partir de 61»— así que el tramo es
 * `desde ≤ días ≤ hasta` y cada uno empieza en el «hasta» del anterior **más
 * uno**: `{0,30} {31,60} {61,null}`. Mezclar los dos criterios descoloca justo
 * los días del borde, y ahí no salta ningún error.
 *
 * @param {Array<{from:number,to:number|null,pct:number}>} tiers
 * @returns {string[]}
 */
function validateExtrajudicialTiers(tiers) {
  if (tiers === null || tiers === undefined) return [];
  if (!Array.isArray(tiers)) return ['El escalado tiene que ser una lista de tramos'];
  if (tiers.length === 0) return [];
  if (tiers.length > MAX_TIERS) return [`Como mucho ${MAX_TIERS} tramos`];

  const errors = [];
  tiers.forEach((t, i) => {
    const n = i + 1;
    const last = i === tiers.length - 1;
    if (!t || !isDays(t.from)) errors.push(`Tramo ${n}: «desde» tiene que ser un número de días`);
    if (!t || !isPct(t.pct)) errors.push(`Tramo ${n}: el % tiene que estar entre 0 y 100`);
    if (t && t.to === null) {
      if (!last) errors.push(`Tramo ${n}: solo el último puede no tener límite`);
    } else if (!t || !isDays(t.to)) {
      errors.push(`Tramo ${n}: «hasta» tiene que ser un número de días`);
    } else if (isDays(t.from) && t.to <= t.from) {
      errors.push(`Tramo ${n}: «hasta» tiene que ser mayor que «desde»`);
    }
    if (i === 0 && t && t.from !== 0) errors.push('El primer tramo tiene que empezar en 0 días');
    // El escalado tiene que cubrir TODOS los días: si el último tiene límite, un
    // cobro más viejo no cae en ningún tramo y se le aplicaría el último sin que
    // nada avise. Por eso el último no puede tener «hasta».
    if (last && t && t.to !== null) errors.push('El último tramo no puede tener límite: deja «hasta» vacío');
    // Inclusivos: el siguiente empieza en el «hasta» del anterior MÁS UNO.
    if (i > 0 && t && tiers[i - 1] && isDays(tiers[i - 1].to) && t.from !== tiers[i - 1].to + 1) {
      errors.push(`Tramo ${n}: tiene que empezar en ${tiers[i - 1].to + 1} días, justo después del tramo ${i}`);
    }
  });
  return errors;
}

/**
 * El % que le toca a un cobro por sus días de impago, y en qué tramo cae.
 *
 * Sin días (`null`) cae en el PRIMER tramo, que es el más barato: no se le cobra
 * de más al cliente por un dato que nos falta.
 *
 * @returns {{index:number, pct:number} | null}  null = no hay escalado.
 */
function pctForDays(days, tiers) {
  if (!Array.isArray(tiers) || !tiers.length) return null;
  if (days === null || days === undefined || !Number.isFinite(Number(days))) {
    return { index: 0, pct: tiers[0].pct };
  }
  const d = Number(days);
  const index = tiers.findIndex((t) => d >= t.from && (t.to === null || d <= t.to));
  // Por encima del último tramo con límite: se queda en el último.
  const i = index === -1 ? tiers.length - 1 : index;
  return { index: i, pct: tiers[i].pct };
}

/** Los errores de unos precios, en castellano. Vacío = válidos. */
function validateRecoveryPricing(p) {
  if (!p || typeof p !== 'object') return ['Faltan los precios'];
  const errors = [];
  if (!isPct(p.extrajudicial_pct)) errors.push('El % extrajudicial tiene que estar entre 0 y 100');
  if (p.bill_judicial) {
    if (!isPct(p.judicial_pct)) errors.push('El % judicial tiene que estar entre 0 y 100');
  } else if (p.judicial_pct !== null && p.judicial_pct !== undefined && !isPct(p.judicial_pct)) {
    errors.push('El % judicial tiene que estar entre 0 y 100');
  }
  if (p.monthly_cap !== null && p.monthly_cap !== undefined && (!isMoney(p.monthly_cap) || p.monthly_cap === 0)) {
    errors.push('El tope al mes tiene que ser un importe mayor que 0, o quedarse vacío');
  }
  return errors;
}

/**
 * Lo cobrado y los honorarios de cada tramo del escalado. Un tramo sin cobros
 * sale igual, a 0: la pantalla enseña la tabla entera.
 *
 * @param {Array<{amount:number, days_overdue:number|null}>} payments
 * @param {Array<{from:number,to:number|null,pct:number}>} tiers
 */
function tierBreakdown(payments, tiers) {
  const acc = tiers.map((t) => ({ from: t.from, to: t.to, pct: t.pct, collected: 0, fee: 0 }));
  for (const p of payments) {
    const amount = Number(p?.amount) || 0;
    if (!amount) continue;
    const hit = pctForDays(p?.days_overdue, tiers);
    if (!hit) continue;
    acc[hit.index].collected = round2(acc[hit.index].collected + amount);
  }
  return acc.map((t) => ({ ...t, fee: round2((t.collected * t.pct) / 100) }));
}

/**
 * Los honorarios de un mes.
 *
 * Con escalado (`pricing.extrajudicial_tiers`) hay que pasar además
 * `collected.extrajudicial_payments`: los cobros con sus `days_overdue`, porque
 * el % depende de los días de cada uno. Sin ellos se usa el % plano.
 *
 * @param {{extrajudicial:number, judicial:number,
 *          extrajudicial_payments?:Array<{amount:number,days_overdue:number|null}>}} collected
 * @param {object|null} pricing
 * @returns {null | {
 *   extrajudicial: {collected, pct, fee},
 *   judicial: {collected, pct, fee, billed},
 *   subtotal, capped, cap, fee, vat, fee_with_vat
 * }}  null = sin precios definidos.
 */
function recoveryFees(collected, pricing) {
  if (!pricing || validateRecoveryPricing(pricing).length) return null;
  const extra = round2(Number(collected?.extrajudicial) || 0);
  const jud = round2(Number(collected?.judicial) || 0);

  // Con escalado el fee se calcula cobro a cobro, porque el % depende de los días
  // de cada uno. Sin los cobros no se puede: se avisa y se cae al % plano, que es
  // lo que hacía antes, en vez de devolver un importe inventado.
  const tiers = hasTiers(pricing) ? pricing.extrajudicial_tiers : null;
  const payments = Array.isArray(collected?.extrajudicial_payments) ? collected.extrajudicial_payments : null;
  const byTier = tiers && payments ? tierBreakdown(payments, tiers) : null;

  const extraFee = byTier
    ? round2(byTier.reduce((s, t) => s + t.fee, 0))
    : round2((extra * pricing.extrajudicial_pct) / 100);
  const judBilled = Boolean(pricing.bill_judicial);
  const judFee = judBilled ? round2((jud * pricing.judicial_pct) / 100) : 0;

  const subtotal = round2(extraFee + judFee);
  const cap = pricing.monthly_cap ?? null;
  const capped = cap !== null && subtotal > cap;
  const fee = capped ? round2(cap) : subtotal;

  return {
    // Con escalado no hay UN porcentaje: `pct` va a null y el desglose en `tiers`,
    // para que la pantalla enseñe lo cobrado en cada tramo y no un % que no existe.
    extrajudicial: {
      collected: extra,
      pct: byTier ? null : pricing.extrajudicial_pct,
      fee: extraFee,
      tiers: byTier,
    },
    judicial: { collected: jud, pct: judBilled ? pricing.judicial_pct : null, fee: judFee, billed: judBilled },
    subtotal,
    capped,
    cap,
    fee,
    vat: round2(fee * VAT_RATE),
    fee_with_vat: round2(fee * (1 + VAT_RATE)),
  };
}

module.exports = {
  VAT_RATE,
  MAX_TIERS,
  validateRecoveryPricing,
  validateExtrajudicialTiers,
  hasTiers,
  pctForDays,
  recoveryFees,
};
